<template>
  <div style="position: relative; height: 100%">
    <div :id="mapId" style="height: 100%" @keydown.delete="deleteSelected" />
    <div class="top-right">
      <div class="mapboxgl-ctrl-group mapboxgl-ctrl">
        <button v-for="(btn, ind) in buttons.filter((f) => $listeners[f.event])" class="mapbox-gl-draw_ctrl-draw-btn" :class="{ 'mapbox-gl-draw_bg': btn.isOn }" :title="btn.title" @click="btn.isOn ? buttonOff(btn) : buttonOn(btn)" :key="ind">
          <v-icon style="padding-top: 3px">{{ btn.icon }}</v-icon>
        </button>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '../../libraries/mapbox-gl-draw/index.js';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../../libraries/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import { circlePolyline } from '../../libraries/geometric';
import DrawRectangle from './mapBox/drawRectangle';
import SimpleSelectMode from './mapBox/simpleSelectModeOverride';
import DirectMode from './mapBox/directModeOverride';
import DrawCircle from './mapBox/drawCircle';
var circle = require('./mapBox/circle');
var rectangle = require('./mapBox/rectangle');
import createSupplementaryPoints from '../../libraries/mapbox-gl-draw/src/lib/create_supplementary_points';

export default {
  props: ['accessToken', 'mapOptions', 'selectedStates'],
  data() {
    return {
      mapId: Math.random().toString(),
      map: null,
      drawControl: null,
      selectedButton: null,
      buttons: [
        {
          title: 'Select states',
          onFnc: this.showStatesLayer,
          offFnc: this.hideStatesLayer,
          event: 'state-selected',
          icon: 'mdi-image-filter-black-white'
        },
        {
          title: 'Select rectangle area',
          onFnc: this.drawRectangle,
          offFnc: this.cancelDrawRectangle,
          event: 'rectangle-selected',
          icon: 'mdi-crop-landscape'
        },
        {
          title: 'Select circle area',
          onFnc: this.drawCircle,
          offFnc: this.cancelDrawCircle,
          event: 'circle-selected',
          icon: 'mdi-adjust'
        }
      ]
    };
  },
  methods: {
    addControls() {
      this.drawControl = new MapboxDraw({
        userProperties: true,
        displayControlsDefault: false,
        modes: {
          ...MapboxDraw.modes,
          draw_circle: DrawCircle,
          direct_select: DirectMode,
          simple_select: SimpleSelectMode,
          draw_rectangle: DrawRectangle
        }
      });
      this.map.addControl(this.drawControl);

      this.map.on('draw.create', this.drawingCreated);
      // this.map.on('draw.delete', this.drawDeleted);
      this.map.on('draw.update', this.drawingCreated);

      this.addStatesLayers();

      this.drawControl.addCircle = (center, r) => {
        var id = this.drawControl.add({
          type: 'Feature',
          properties: { isCircle: true, center },
          geometry: {
            type: 'Polygon',
            coordinates: [circlePolyline(center, r)]
          }
        });
        this.drawControl.setFeatureProperty(
          id,
          'dragVertex',
          circle.dragVertex
        );
        this.drawControl.setFeatureProperty(
          id,
          'createSupplementaryPoints',
          circle.createSupplementaryPoints
        );
        this.drawControl.setFeatureProperty(
          id,
          'moveFeature',
          circle.moveFeature
        );
      };

      this.drawControl.addRectangle = (neLngLat, swLngLat) => {
        var id = this.drawControl.add({
          type: 'Feature',
          properties: { isRectangle: true },
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                swLngLat,
                [swLngLat[0], neLngLat[1]],
                neLngLat,
                [neLngLat[0], swLngLat[1]],
                swLngLat
              ]
            ]
          }
        });
        this.drawControl.setFeatureProperty(
          id,
          'dragVertex',
          rectangle.dragVertex
        );
        this.drawControl.setFeatureProperty(
          id,
          'moveFeature',
          rectangle.moveFeature
        );
        this.drawControl.setFeatureProperty(
          id,
          'createSupplementaryPoints',
          createSupplementaryPoints
        );
      };

      this.$emit('map-created', this.map, mapboxgl, this.drawControl);
    },
    addStatesLayers() {
      this.map.addSource('states', {
        type: 'geojson',
        data: require('../../data/maps/us-states.json')
      });

      this.map.addLayer({
        id: 'states',
        source: 'states',
        type: 'fill',
        layout: { visibility: 'none' },
        paint: {
          'fill-outline-color': 'rgba(0,0,0,0)',
          'fill-color': 'rgba(0,0,0,0)'
        }
      });

      this.map.addLayer({
        id: 'states-highlighted',
        type: 'fill',
        source: 'states',
        paint: {
          'fill-outline-color': '#484896',
          'fill-color': '#6e599f',
          'fill-opacity': 0.75
        },
        layout: { visibility: 'visible' },
        filter: ['in', 'code', '']
      });

      this.map.on('click', 'states', e => {
        this.removeDrawings();

        var code = e.features[0].properties.code;
        var selectedStates = (this.selectedStates || []).includes(code)
          ? this.selectedStates.filter(f => f !== code)
          : (this.selectedStates || []).concat(code);

        this.$emit('state-selected', selectedStates);
      });
      this.map.setFilter('states-highlighted', [
        'in',
        'code',
        ...(this.selectedStates || [])
      ]);
    },
    showStatesLayer() {
      this.map.getCanvas().style.cursor = 'pointer';
      this.map.setLayoutProperty('states', 'visibility', 'visible');
    },
    hideStatesLayer() {
      this.map.getCanvas().style.cursor = '';
      this.map.setLayoutProperty('states', 'visibility', 'none');
    },
    drawCircle() {
      this.drawControl.changeMode('draw_circle');
    },
    cancelDrawCircle() {
      this.drawControl.changeMode('simple_select');
    },
    deleteSelected() {
      var selected = this.drawControl.getSelected().features;
      this.drawControl.delete(selected.map(f => f.id));

      if (selected.find(f => f.properties.isCircle))
        this.$emit('circle-selected', null);
      if (selected.find(f => f.properties.isRectangle))
        this.$emit('rectangle-selected', null);
    },
    drawingCreated(e) {
      this.removeDrawings(e);
      this.removeStates();

      var circ = e.features.find(f => f.properties.isCircle);
      if (circ) {
        let lngs = circ.geometry.coordinates[0].map(f => f[0]);
        let lats = circ.geometry.coordinates[0].map(f => f[1]);

        this.$emit('circle-selected', {
          ...circ.properties,
          maxLat: lats.max(),
          minLat: lats.min(),
          maxLng: lngs.max(),
          minLng: lngs.min(),
          r: circ.properties.radius
        });
      }

      if (e.features.find(f => f.properties.isRectangle)) {
        let lngs = e.features[0].geometry.coordinates[0].map(f => f[0]);
        let lats = e.features[0].geometry.coordinates[0].map(f => f[1]);
        this.$emit('rectangle-selected', {
          neLngLat: [lngs.max(), lats.max()],
          swLngLat: [lngs.min(), lats.min()]
        });
      }
      var onBtn = this.buttons.find(f => f.isOn);
      if (onBtn) onBtn.isOn = false;
    },
    removeDrawings(except) {
      var removeIds = this.drawControl
        .getAll()
        .features.map(f => f.id)
        .filter(
          f => !((except || {}).features || []).map(g => g.id).includes(f)
        );

      if (removeIds.length) {
        var removed = this.drawControl
          .getAll()
          .features.filter(f => removeIds.includes(f.id));
        if (removed.find(f => f.properties.isCircle))
          this.$emit('circle-selected', null);
        if (removed.find(f => f.properties.isRectangle))
          this.$emit('rectangle-selected', null);
        this.drawControl.delete(removeIds);
      }
    },
    drawRectangle() {
      this.drawControl.changeMode('draw_rectangle');
    },
    cancelDrawRectangle() {
      this.drawControl.changeMode('simple_select');
    },
    removeStates() {
      this.$emit('state-selected', []);
    },
    buttonOn(btn) {
      var onBtn = this.buttons.find(f => f.isOn);
      if (onBtn) onBtn.offFnc();
      this.buttons.forEach(f => this.$set(f, 'isOn', btn === f));
      btn.onFnc();
    },
    buttonOff(btn) {
      btn.offFnc();
      btn.isOn = false;
    }
  },
  watch: {
    selectedStates() {
      this.map.setFilter('states-highlighted', [
        'in',
        'code',
        ...(this.selectedStates || [])
      ]);
    }
  },
  mounted() {
    mapboxgl.accessToken = this.accessToken;
    this.map = new mapboxgl.Map({
      container: this.mapId, // container id
      style: 'mapbox://styles/simunovic-marko/ckgw4fuzl4p0719pa2kue645r',
      center: [-97, 39],
      zoom: 3.4,
      maxBounds: [[-180, 10], [-31, 75]],
      ...(this.mapOptions || {})
    });
    this.map.on('load', event => this.addControls(event));
  }
};
</script>

<style lang="scss">
.mapbox-gl-draw_bg {
  background: $color-gray-2 !important;
}
</style>

<style scoped>
.top-right {
  position: absolute;
  top: 0;
  right: 0;
  margin: 10px 10px 0 0;
}
</style>